<template>
  <header class="app-topbar" :class="isDarkTheme ? 'app-topbar--dark' : 'app-topbar--light'">
    <!-- ── Esquerda: toggle + logo eP + breadcrumbs ── -->
    <div class="app-topbar__left">
      <button
        class="topbar-toggle"
        :aria-label="isSidebarMinimized ? 'Abrir menu' : 'Fechar menu'"
        @click="$emit('toggle-sidebar')"
      >
        <span class="material-icons">{{ isSidebarMinimized ? 'menu' : 'menu_open' }}</span>
      </button>

      <div
        class="topbar-logo"
        role="link"
        tabindex="0"
        @click="$router.push({ name: 'home' })"
        @keydown.enter="$router.push({ name: 'home' })"
      >
        <img src="/ep.png" alt="eP" class="topbar-logo__img" />
      </div>

      <WorkspaceSwitcher />

      <nav class="topbar-breadcrumbs" aria-label="Caminho de navegação">
        <span v-for="(crumb, i) in breadcrumbs" :key="i" class="topbar-breadcrumbs__segment">
          <span v-if="i > 0" class="topbar-breadcrumbs__sep" aria-hidden="true">/</span>
          <RouterLink v-if="crumb.to && i < breadcrumbs.length - 1" :to="crumb.to" class="topbar-breadcrumbs__link">{{
            crumb.label
          }}</RouterLink>
          <span
            v-else
            class="topbar-breadcrumbs__current"
            :aria-current="i === breadcrumbs.length - 1 ? 'page' : undefined"
            >{{ crumb.label }}</span
          >
        </span>
      </nav>
    </div>

    <!-- ── Centro: marca da área ── -->
    <div class="app-topbar__center" aria-hidden="true">
      <AreaBrand class="topbar-brand" :name="brandName" :ink="!isDarkTheme" />
    </div>

    <!-- ── Direita: ações ── -->
    <div class="app-topbar__right">
      <AppNavbarActions />
    </div>
  </header>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppNavbarActions from './components/AppNavbarActions.vue'
import WorkspaceSwitcher from './components/WorkspaceSwitcher.vue'
import AreaBrand from '@/components/brand/AreaBrand.vue'
import { WORKSPACES } from '@/constants/workspaces'
import { useEffectiveWorkspace } from '@/composables/useEffectiveWorkspace'
import NavigationRoutes from '../sidebar/NavigationRoutes'

defineEmits(['toggle-sidebar'])

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const isSidebarMinimized = computed(() => store.state.isSidebarMinimized)

// Theme sync
const currentTheme = ref('light')

function syncTheme() {
  try {
    if (typeof window !== 'undefined' && window.getAppTheme) {
      currentTheme.value = window.getAppTheme() ?? 'light'
    }
  } catch (e) {}
}

const isDarkTheme = computed(() => currentTheme.value === 'dark')

// Marca central: toda área tem a sua, montada pelo AreaBrand a partir da mesma
// arte ("easy" + palete) mais o nome da ferramenta. Antes só CRM e Service Desk
// tinham PNG — quem entrava em Indicadores ou Monitoramento via "EasyCRM" no
// topo, e AI Agents caía num bloco de texto que não parecia com as outras.
const currentWorkspace = useEffectiveWorkspace()
const brandName = computed(() => (WORKSPACES.find((w) => w.key === currentWorkspace.value) ?? WORKSPACES[0]).brand)

onMounted(() => {
  syncTheme()
  window.addEventListener('app-theme-changed', syncTheme)
})

onBeforeUnmount(() => {
  window.removeEventListener('app-theme-changed', syncTheme)
})

// Breadcrumbs
const breadcrumbs = computed(() => {
  const currentName = route.name

  for (const item of NavigationRoutes.routes) {
    if (item.name === currentName) {
      return [{ label: t(item.displayName), to: null }]
    }
    if (item.children) {
      for (const child of item.children) {
        if (child.name === currentName) {
          return [
            { label: t(item.displayName), to: { name: item.children[0].name } },
            { label: t(child.displayName), to: null },
          ]
        }
      }
    }
  }

  // Tela fora do menu (ex.: detalhe) que declara o item pai em meta.breadcrumbParent.
  const parentName = route.meta.breadcrumbParent
  const parent = parentName && NavigationRoutes.routes.find((item) => item.name === parentName)
  if (parent) {
    return [
      { label: t(parent.displayName), to: { name: parent.name } },
      { label: String(route.meta.breadcrumbLabel ?? ''), to: null },
    ]
  }

  return [{ label: t('navigationRoutes.home'), to: null }]
})
</script>

<style lang="scss" scoped>
.topbar-brand {
  // Altura, não largura: o bloco é mais alto quanto mais curto for o nome da
  // área, então fixar a largura faria "CRM" vazar da barra de 60px. A arte vem
  // recortada na tinta, então estes 44px são o tamanho aparente da marca — sem
  // a sobra de transparência que obrigava cada PNG a ter uma altura própria.
  height: 44px;
}

.app-topbar {
  height: 60px;
  flex-shrink: 0;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 12px;
  gap: 12px;
  border: 1px solid var(--shell-glass-border);
  box-sizing: border-box;
  position: relative;

  @supports (backdrop-filter: blur()) {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  &--dark {
    background: var(--shell-glass-bg);
    color: #e7e9ef;
    .topbar-toggle {
      color: #e7e9ef;
    }
    .topbar-breadcrumbs__sep,
    .topbar-breadcrumbs__link {
      color: rgba(231, 233, 239, 0.45);
    }
    .topbar-breadcrumbs__current {
      color: #e7e9ef;
    }
  }

  &--light {
    background: var(--shell-glass-bg);
    color: #15171d;
    .topbar-toggle {
      color: #15171d;
    }
    .topbar-breadcrumbs__sep,
    .topbar-breadcrumbs__link {
      color: rgba(21, 23, 29, 0.45);
    }
    .topbar-breadcrumbs__current {
      color: #15171d;
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    overflow: hidden;
    min-width: 0;
    z-index: 1;
  }

  // Logo centralizado com position absolute para não afetar o layout flex
  &__center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    pointer-events: none;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    z-index: 1;
  }
}

// ── Botão toggle ─────────────────────────────────────────────
.topbar-toggle {
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.15s;
  padding: 0;

  &:hover {
    background: var(--shell-item-hover);
  }
  &:focus-visible {
    outline: 2px solid #ff4d2e;
    outline-offset: 2px;
  }

  .material-icons {
    font-size: 20px;
  }
}

// ── Logo eP (canto esquerdo) ──────────────────────────────────
.topbar-logo {
  cursor: pointer;
  flex-shrink: 0;

  &__img {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    object-fit: contain;
    display: block;
  }

  &:focus-visible {
    outline: 2px solid #ff4d2e;
    outline-offset: 2px;
    border-radius: 10px;
  }
}

// ── Breadcrumbs ──────────────────────────────────────────────
.topbar-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  overflow: hidden;

  &__segment {
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    overflow: hidden;

    &:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__sep {
    opacity: 0.4;
  }

  &__link {
    text-decoration: none;
    opacity: 0.55;
    transition: opacity 0.15s;
    &:hover {
      opacity: 0.85;
    }
  }

  &__current {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
